// Dynamic IG promo reels — route: /promo/:n  (n = 9..28)
// Each reel = { id, slides }. Slide render functions use shared helpers
// for consistent typography, motion, and IG-safe layout.
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { PromoStage, BgPhoto, PHOTOS, FULL_PB } from "@/pages/promo/_stage";

// ---------------- Render helpers ----------------

const Kicker = ({ children, light = false }) => (
  <motion.p
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.4 }}
    className={`uppercase tracking-[0.5em] text-sm font-bold mb-6 ${light ? "text-white" : "text-[#FF1B91]"}`}
  >
    / {children}
  </motion.p>
);

const Headline = ({ lines, size = 140, delay = 0.15 }) => (
  <motion.h2
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay }}
    className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter"
    style={{ fontSize: `${size}px` }}
  >
    {lines.map((l, i) => (
      <span key={i}>
        {l.brush ? (
          <span className="font-brush font-normal normal-case -rotate-3 inline-block" style={{ fontSize: `${size + 20}px`, color: l.color || undefined }}>{l.text}</span>
        ) : (
          <span style={{ color: l.color || undefined }}>{l.text}</span>
        )}
        {i < lines.length - 1 && <br />}
      </span>
    ))}
  </motion.h2>
);

const Sub = ({ children, delay = 0.55, light = false }) => (
  <motion.p
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.5, delay }}
    className={`mt-8 text-2xl leading-snug ${light ? "text-white" : "text-white/65"}`}
  >
    {children}
  </motion.p>
);

const NumberedList = ({ items }) => (
  <div className="mt-2">
    {items.map((it, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: 0.2 + i * 0.18 }}
        className="flex items-center gap-5 py-4 border-b border-white/10"
      >
        <span className="font-display font-black text-[#FF1B91] text-3xl tracking-tighter w-12">0{i + 1}</span>
        <span className="font-display font-black uppercase text-white text-3xl tracking-tighter leading-[1.0]">{it}</span>
      </motion.div>
    ))}
  </div>
);

const CardsGrid = ({ cards, cols = 2 }) => (
  <div className={`grid gap-3 mt-2 ${cols === 2 ? "grid-cols-2" : cols === 3 ? "grid-cols-3" : "grid-cols-1"}`}>
    {cards.map((c, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, delay: 0.2 + i * 0.1 }}
        className="border border-[#FF1B91]/40 bg-[#FF1B91]/5 p-5"
      >
        {c.top && <p className="text-white/55 text-[10px] uppercase tracking-[0.3em] font-bold">{c.top}</p>}
        <p className="font-display font-black uppercase text-white text-2xl tracking-tighter leading-none mt-1">{c.title}</p>
        {c.bottom && <p className="font-display font-black text-[#FF1B91] text-xl tracking-tighter mt-2">{c.bottom}</p>}
      </motion.div>
    ))}
  </div>
);

const BigStat = ({ value, sub, brush = false, accent = "white" }) => (
  <div className="flex flex-col items-center justify-center w-full h-full text-center">
    <motion.h2
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.55, delay: 0.15 }}
      className="font-display font-black uppercase leading-[0.85] tracking-tighter"
      style={{ fontSize: "280px", color: accent }}
    >
      {brush ? (
        <span className="font-brush font-normal normal-case -rotate-3 inline-block">{value}</span>
      ) : value}
    </motion.h2>
    {sub && (
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="mt-6 text-3xl font-bold uppercase tracking-tight"
        style={{ color: accent }}
      >
        {sub}
      </motion.p>
    )}
  </div>
);

// ---------------- Slide shapes ----------------

// Standard 'text only' slide: kicker + headline + sub
const T = ({ kicker, lines, size = 140, sub, kickerLight }) => () => (
  <div className="flex flex-col justify-center w-full h-full">
    {kicker && <Kicker light={kickerLight}>{kicker}</Kicker>}
    <Headline lines={lines} size={size} />
    {sub && <Sub light={kickerLight}>{sub}</Sub>}
  </div>
);

// Title (centered, big brush + block, like an opener)
const TT = ({ kicker, lines, size = 200, kickerLight = true }) => () => (
  <div className="flex flex-col items-center justify-center w-full h-full text-center">
    {kicker && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className={`uppercase tracking-[0.5em] text-sm font-semibold mb-8 ${kickerLight ? "text-white/85" : "text-[#FF1B91]"}`}>/ {kicker}</motion.p>}
    <Headline lines={lines} size={size} />
  </div>
);

// Numbered list slide
const L = ({ kicker, items, kickerLight }) => () => (
  <div className="flex flex-col justify-center w-full h-full">
    <Kicker light={kickerLight}>{kicker}</Kicker>
    <NumberedList items={items} />
  </div>
);

// Cards grid slide
const G = ({ kicker, cards, cols = 2, kickerLight }) => () => (
  <div className="flex flex-col justify-center w-full h-full">
    <Kicker light={kickerLight}>{kicker}</Kicker>
    <CardsGrid cards={cards} cols={cols} />
  </div>
);

// Big stat slide
const S = ({ kicker, value, sub, brush, accent, kickerLight }) => () => (
  <div className="flex flex-col items-center justify-center w-full h-full text-center">
    {kicker && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className={`uppercase tracking-[0.5em] text-sm font-bold mb-6 ${kickerLight ? "text-white" : "text-[#FF1B91]"}`}>/ {kicker}</motion.p>}
    <BigStat value={value} sub={sub} brush={brush} accent={accent} />
  </div>
);

// Photo background slide (full-bleed)
const P = ({ photo = "hero", kb = "in", overlay = "b", kicker, lines, size = 140, sub }) => () => (
  <>
    <BgPhoto src={PHOTOS[photo]} kenBurns={kb} />
    <div className={`absolute inset-0 ${overlay === "b" ? "bg-gradient-to-t from-black/85 via-black/55 to-black/20" : overlay === "t" ? "bg-gradient-to-b from-black/40 via-black/55 to-black/90" : "bg-black/70"}`} />
    <div className="absolute inset-0 flex flex-col justify-end px-16" style={{ paddingBottom: FULL_PB + 20 }}>
      {kicker && <Kicker>{kicker}</Kicker>}
      <Headline lines={lines} size={size} />
      {sub && <Sub>{sub}</Sub>}
    </div>
  </>
);

// Convenience builders
const pink = "#FF1B91";
const dark = "#0A0A0A";
const cta = (kicker = "Pull up.", url = "sweatwithslay.com") => ({
  id: "cta", duration: 3000, bg: pink, render: TT({ kicker, lines: [{ text: "Pull" }, { text: "up.", brush: true }], size: 220, kickerLight: true }),
});
const ctaDark = (kicker = "Slide in") => ({
  id: "cta", duration: 3000, bg: dark, render: TT({ kicker, lines: [{ text: "Sweat" }, { text: "With Slay.", brush: true, color: pink }], size: 180 }),
});

// ---------------- The 20 reels (9 → 28) ----------------

const REELS = {
  // 9 — Stop scrolling (confrontational hook)
  "9": {
    title: "Stop scrolling",
    slides: [
      { id: "hook", duration: 2200, bg: pink, render: TT({ kicker: "Read this", lines: [{ text: "Stop" }, { text: "scrolling.", brush: true }], size: 220 }) },
      { id: "truth", duration: 2600, bg: dark, render: T({ kicker: "Be honest", lines: [{ text: "You've said" }, { text: "\"next Monday\"", color: pink }, { text: "for 6 weeks." }], size: 110 }) },
      { id: "fix", duration: 2400, bg: dark, render: T({ kicker: "Here's the fix", lines: [{ text: "Stop" }, { text: "deciding.", color: pink }, { text: "Get the plan." }], size: 130 }, "Decision fatigue is why you quit.") },
      { id: "what", duration: 2600, bg: dark, render: L({ kicker: "What I give you", items: ["Programming, weekly", "Form check, every lift", "Voice notes back, fast"] }) },
      { id: "close", duration: 2200, bg: pink, render: S({ kicker: "Time to act", value: "15 min.", sub: "That's how long the intake takes.", kickerLight: true }) },
      cta("Pull up. Now."),
    ],
  },
  // 10 — If you've quit before
  "10": {
    title: "Quit before? Read this",
    slides: [
      { id: "hook", duration: 2200, bg: dark, full: true, render: P({ photo: "about", kb: "out", kicker: "Real talk", lines: [{ text: "If you've" }, { text: "quit before", color: pink }, { text: "read this." }], size: 130 }) },
      { id: "why", duration: 2400, bg: dark, render: T({ kicker: "Why you quit", lines: [{ text: "The plan was" }, { text: "too perfect.", color: pink }], size: 150, kickerLight: false }, "It assumed you'd never miss a day.") },
      { id: "real", duration: 2400, bg: dark, render: T({ kicker: "Mine isn't", lines: [{ text: "My plans" }, { text: "bend.", color: pink }, { text: "Don't break." }], size: 150 }, "Travel weeks. Sick days. Cycles. We adjust.") },
      { id: "promise", duration: 2400, bg: pink, render: T({ kicker: "Promise", lines: [{ text: "You'll miss" }, { text: "workouts.", brush: true }, { text: "It's fine." }], size: 140, kickerLight: true }, "Consistency, not perfection. We track 70 of 90.") },
      { id: "spots", duration: 1800, bg: dark, render: S({ kicker: "Cohort open", value: "15", sub: "Spots in the 90-day Slay." }) },
      cta(),
    ],
  },
  // 11 — The fitness industry lies to women
  "11": {
    title: "Industry lies to women",
    slides: [
      { id: "hook", duration: 2200, bg: pink, render: TT({ kicker: "Hot take", lines: [{ text: "The industry" }, { text: "lies", brush: true }, { text: "to women." }], size: 160 }) },
      { id: "lie1", duration: 2200, bg: dark, render: T({ kicker: "Lie 01", lines: [{ text: "\"Lift light." }, { text: "Tone up.\"", color: pink }], size: 150 }, "Tone is muscle + low body fat. You need to lift heavy.") },
      { id: "lie2", duration: 2200, bg: dark, render: T({ kicker: "Lie 02", lines: [{ text: "\"Eat less." }, { text: "Cardio more.\"", color: pink }], size: 150 }, "You'll lose the muscle. Then plateau. Then quit.") },
      { id: "lie3", duration: 2200, bg: dark, render: T({ kicker: "Lie 03", lines: [{ text: "\"Buy this" }, { text: "tea.\"", color: pink }], size: 180 }, "There is no shortcut. There is a system.") },
      { id: "truth", duration: 2400, bg: pink, render: T({ kicker: "The truth", lines: [{ text: "Strength" }, { text: "first.", brush: true }, { text: "Always." }], size: 170, kickerLight: true }) },
      ctaDark("Train like it"),
    ],
  },
  // 12 — Why women don't lift heavy
  "12": {
    title: "Why women don't lift",
    slides: [
      { id: "open", duration: 2200, bg: dark, render: TT({ kicker: "Real question", lines: [{ text: "Why" }, { text: "won't", brush: true, color: pink }, { text: "you lift?" }], size: 180, kickerLight: false }) },
      { id: "myth", duration: 2200, bg: dark, render: T({ kicker: "The lie you're told", lines: [{ text: "\"You'll get" }, { text: "bulky.\"", color: pink }], size: 170 }, "It takes years + a calorie surplus. Not a tuesday.") },
      { id: "what", duration: 2400, bg: dark, render: T({ kicker: "What lifting actually does", lines: [{ text: "Builds" }, { text: "shape.", color: pink }, { text: "Burns fat." }], size: 150 }, "And keeps both, year after year.") },
      { id: "proof", duration: 2200, bg: pink, render: S({ value: "200%", sub: "More calorie burn at rest vs cardio-only.", kickerLight: true, kicker: "Receipts" }) },
      { id: "start", duration: 1800, bg: dark, render: T({ kicker: "Where to start", lines: [{ text: "With a plan." }, { text: "Not vibes.", color: pink }], size: 140 }) },
      cta("Get the plan"),
    ],
  },
  // 13 — 3 mistakes I see daily
  "13": {
    title: "3 mistakes I see",
    slides: [
      { id: "open", duration: 2000, bg: dark, render: TT({ kicker: "Form check", lines: [{ text: "3 mistakes" }, { text: "I see", brush: true, color: pink }, { text: "every day." }], size: 150, kickerLight: false }) },
      { id: "m1", duration: 2400, bg: pink, render: T({ kicker: "Mistake 01", lines: [{ text: "Half-rep" }, { text: "squats.", brush: true }], size: 180, kickerLight: true }, "If you can't go below parallel — it's a mobility fix, not an ego fix.") },
      { id: "m2", duration: 2400, bg: dark, render: T({ kicker: "Mistake 02", lines: [{ text: "Cardio" }, { text: "before lifts.", color: pink }], size: 140 }, "Tank the lift, tank the result. Lift first. Cardio after.") },
      { id: "m3", duration: 2400, bg: pink, render: T({ kicker: "Mistake 03", lines: [{ text: "No" }, { text: "rest days.", brush: true }], size: 170, kickerLight: true }, "Recovery is when muscle grows. Skip it, plateau in week 3.") },
      { id: "fix", duration: 1800, bg: dark, render: S({ kicker: "Fix all 3 in 24h", value: "DM", sub: "I'll send you the audit checklist." }) },
      cta(),
    ],
  },
  // 14 — Macros aren't scary
  "14": {
    title: "Macros aren't scary",
    slides: [
      { id: "hook", duration: 2200, bg: pink, render: TT({ kicker: "Nutrition 101", lines: [{ text: "Macros" }, { text: "aren't", brush: true }, { text: "scary." }], size: 180 }) },
      { id: "p", duration: 1800, bg: dark, render: S({ kicker: "Protein", value: "0.8–1g", sub: "per lb of bodyweight, daily.", accent: "#FF1B91" }) },
      { id: "c", duration: 1800, bg: dark, render: S({ kicker: "Carbs", value: "1–2g", sub: "per lb, depending on training day." }) },
      { id: "f", duration: 1800, bg: dark, render: S({ kicker: "Fats", value: "0.3g", sub: "per lb. Fill the rest of your calories.", accent: "#FF1B91" }) },
      { id: "real", duration: 2200, bg: pink, render: T({ kicker: "That's it", lines: [{ text: "No food is" }, { text: "off limits.", brush: true }], size: 170, kickerLight: true }, "Just count for 14 days. You'll never need to again.") },
      cta("Want the calculator"),
    ],
  },
  // 15 — Cardio vs lifting
  "15": {
    title: "Cardio vs lifting",
    slides: [
      { id: "hook", duration: 2000, bg: dark, render: TT({ kicker: "Debate, settled", lines: [{ text: "Cardio" }, { text: "vs.", color: pink, brush: true }, { text: "Lifting." }], size: 200, kickerLight: false }) },
      { id: "win", duration: 2600, bg: dark, render: G({ kicker: "For fat loss", cards: [
        { top: "Cardio", title: "Calories now", bottom: "But muscle loss" },
        { top: "Lifting", title: "Calories all day", bottom: "And muscle stays" },
      ] }) },
      { id: "ans", duration: 2400, bg: pink, render: T({ kicker: "The answer", lines: [{ text: "Lift 4x." }, { text: "Walk", brush: true }, { text: "the rest." }], size: 180, kickerLight: true }, "Walk 8–10k steps. Skip the hour on the treadmill.") },
      { id: "why", duration: 2400, bg: dark, render: T({ kicker: "Why this works", lines: [{ text: "More muscle =" }, { text: "more burn.", color: pink }], size: 130 }, "Every day. Not just at the gym.") },
      { id: "p", duration: 1800, bg: dark, render: S({ kicker: "The split", value: "4 + walk", sub: "4 lifts. Daily 8k steps. That's it.", kickerLight: false }) },
      cta(),
    ],
  },
  // 16 — Form check checklist
  "16": {
    title: "Form check checklist",
    slides: [
      { id: "hook", duration: 2200, bg: pink, render: TT({ kicker: "Save this", lines: [{ text: "Form" }, { text: "check.", brush: true }], size: 240 }) },
      { id: "sq", duration: 1800, bg: dark, render: T({ kicker: "Squat", lines: [{ text: "Hip below" }, { text: "knee.", color: pink }], size: 170 }, "If you can't, it's a mobility drill. Not a load drill.") },
      { id: "dl", duration: 1800, bg: dark, render: T({ kicker: "Deadlift", lines: [{ text: "Bar over" }, { text: "midfoot.", color: pink }], size: 170 }, "Shoulders over the bar. Hips push back, not down.") },
      { id: "bp", duration: 1800, bg: dark, render: T({ kicker: "Bench", lines: [{ text: "Bar to" }, { text: "lower chest.", color: pink }], size: 150 }, "Feet planted. Arch the upper back. Eyes under the bar.") },
      { id: "pull", duration: 1800, bg: dark, render: T({ kicker: "Pull-up", lines: [{ text: "Chest to" }, { text: "bar.", color: pink }], size: 200 }, "Pull with the back, not the arms. Full hang at the bottom.") },
      { id: "send", duration: 1600, bg: pink, render: S({ kicker: "Save this reel", value: "↓", sub: "Or DM me your form videos.", kickerLight: true, brush: false }) },
      cta(),
    ],
  },
  // 17 — Spots open this month
  "17": {
    title: "Spots open",
    slides: [
      { id: "hook", duration: 2000, bg: pink, render: TT({ kicker: "Cohort", lines: [{ text: "Doors" }, { text: "open.", brush: true }], size: 240 }) },
      { id: "n", duration: 2200, bg: dark, render: S({ kicker: "Spots this round", value: "8", sub: "That's it. Then waitlist until Q2.", kickerLight: false }) },
      { id: "why", duration: 2400, bg: dark, render: T({ kicker: "Why so few", lines: [{ text: "Real" }, { text: "coaching.", color: pink }, { text: "Not autopilot." }], size: 150 }, "Weekly check-ins. Voice notes. Calls. It doesn't scale to 200.") },
      { id: "fit", duration: 2400, bg: dark, render: L({ kicker: "You'll thrive if", items: ["You want a system", "You want voice-note feedback", "You'll commit 90 days"] }) },
      { id: "act", duration: 2000, bg: pink, render: S({ kicker: "Time to move", value: "DM", sub: "Apply via /intake on the site.", kickerLight: true }) },
      cta(),
    ],
  },
  // 18 — Why I cap at 25
  "18": {
    title: "Why I cap clients",
    slides: [
      { id: "hook", duration: 2200, bg: dark, render: TT({ kicker: "Honest", lines: [{ text: "Why I" }, { text: "cap", brush: true, color: pink }, { text: "at 25." }], size: 200, kickerLight: false }) },
      { id: "math", duration: 2200, bg: dark, render: S({ kicker: "The math", value: "25", sub: "× 30 min/week × 4 = 50 hrs.", kickerLight: false }) },
      { id: "why", duration: 2400, bg: pink, render: T({ kicker: "Why it matters", lines: [{ text: "Your check-in" }, { text: "isn't", brush: true }, { text: "an inbox dump." }], size: 130, kickerLight: true }, "I read the videos. I write the next block. I respond.") },
      { id: "what", duration: 2200, bg: dark, render: L({ kicker: "What you actually get", items: ["Weekly written response", "Plan rewrites monthly", "Voice notes inside 24h"] }) },
      { id: "spots", duration: 1800, bg: dark, render: S({ kicker: "Open right now", value: "3", sub: "Out of 25. Pull up before they go.", kickerLight: false }) },
      cta(),
    ],
  },
  // 19 — What DMs look like
  "19": {
    title: "Inside the DMs",
    slides: [
      { id: "hook", duration: 2200, bg: pink, render: TT({ kicker: "Day in the DMs", lines: [{ text: "What" }, { text: "coaching", brush: true }, { text: "feels like." }], size: 150 }) },
      { id: "mon", duration: 2200, bg: dark, render: T({ kicker: "Monday", lines: [{ text: "\"Squat felt" }, { text: "weird.\"", color: pink }], size: 170 }, "→ I rewatch your video. Send voice note in 4 hours.") },
      { id: "wed", duration: 2200, bg: dark, render: T({ kicker: "Wednesday", lines: [{ text: "\"Travel" }, { text: "all week.\"", color: pink }], size: 170 }, "→ Hotel-friendly swap dropped before your flight lands.") },
      { id: "sun", duration: 2200, bg: dark, render: T({ kicker: "Sunday", lines: [{ text: "\"Bridal" }, { text: "dinner.\"", color: pink }], size: 180 }, "→ \"Eat. Enjoy. Walk after. See you Monday.\"") },
      { id: "tldr", duration: 2200, bg: pink, render: T({ kicker: "Coaching, not content", lines: [{ text: "Real" }, { text: "answers.", brush: true }, { text: "Same day." }], size: 160, kickerLight: true }) },
      cta(),
    ],
  },
  // 20 — Inside Everfit
  "20": {
    title: "Inside Everfit",
    slides: [
      { id: "hook", duration: 2200, bg: dark, full: true, render: P({ photo: "hero", kb: "in", overlay: "b", kicker: "Tour", lines: [{ text: "Inside" }, { text: "the app.", color: pink }], size: 170 }) },
      { id: "f1", duration: 2200, bg: dark, render: T({ kicker: "Programming", lines: [{ text: "Your plan." }, { text: "Tap & go.", color: pink }], size: 150 }, "Every lift, every set, demo videos baked in.") },
      { id: "f2", duration: 2200, bg: dark, render: T({ kicker: "Logging", lines: [{ text: "Numbers" }, { text: "remembered.", color: pink }], size: 150 }, "Last week's load auto-fills. Beat it or match it.") },
      { id: "f3", duration: 2200, bg: dark, render: T({ kicker: "Feedback", lines: [{ text: "Upload form." }, { text: "Get notes.", color: pink }], size: 140 }, "I voice-note back inside 24 hrs.") },
      { id: "free", duration: 2200, bg: pink, render: S({ kicker: "And", value: "Free", sub: "The app is included. Always.", kickerLight: true }) },
      cta(),
    ],
  },
  // 21 — Voice note coaching
  "21": {
    title: "Voice notes",
    slides: [
      { id: "hook", duration: 2200, bg: pink, render: TT({ kicker: "How it actually works", lines: [{ text: "Voice notes" }, { text: ">", brush: true }, { text: "Spreadsheets." }], size: 130 }) },
      { id: "why", duration: 2400, bg: dark, render: T({ kicker: "Why I do it this way", lines: [{ text: "I can hear" }, { text: "you.", color: pink }], size: 200 }, "Tone tells me more than your check-in form ever could.") },
      { id: "you", duration: 2200, bg: dark, render: T({ kicker: "Your side", lines: [{ text: "30 seconds." }, { text: "Done.", color: pink }], size: 170 }, "Talk while you walk. While you cook. While you commute.") },
      { id: "me", duration: 2200, bg: dark, render: T({ kicker: "My side", lines: [{ text: "Same day." }, { text: "Real plan.", color: pink }], size: 170 }, "No 4-day email threads. Just answers.") },
      { id: "go", duration: 2000, bg: pink, render: S({ kicker: "Curious?", value: "DM", sub: "I'll send you a sample voice note from a client.", kickerLight: true }) },
      cta(),
    ],
  },
  // 22 — Why pink
  "22": {
    title: "Why pink?",
    slides: [
      { id: "hook", duration: 2200, bg: pink, render: TT({ kicker: "Brand story", lines: [{ text: "Why" }, { text: "pink?", brush: true }], size: 280 }) },
      { id: "answer", duration: 2400, bg: dark, render: T({ kicker: "The honest answer", lines: [{ text: "Because" }, { text: "soft", brush: true, color: pink }, { text: "is strong." }], size: 160 }) },
      { id: "more", duration: 2400, bg: dark, render: T({ kicker: "And", lines: [{ text: "Lifting heavy" }, { text: "doesn't make you" }, { text: "less feminine.", color: pink }], size: 110 }, "It makes you more you.") },
      { id: "mantra", duration: 2200, bg: pink, render: TT({ kicker: "Mantra", lines: [{ text: "Strong." }, { text: "Soft.", brush: true }, { text: "Both." }], size: 200 }) },
      { id: "tag", duration: 1800, bg: dark, render: S({ kicker: "Brand", value: "SWS", sub: "Sweat With Slay. That's it.", kickerLight: false }) },
      cta(),
    ],
  },
  // 23 — If I had to start over
  "23": {
    title: "If I started over",
    slides: [
      { id: "hook", duration: 2200, bg: dark, render: TT({ kicker: "POV", lines: [{ text: "If I had to" }, { text: "start", brush: true, color: pink }, { text: "over." }], size: 150, kickerLight: false }) },
      { id: "one", duration: 2200, bg: dark, render: T({ kicker: "Step 1", lines: [{ text: "Get on" }, { text: "the floor.", color: pink }], size: 160 }, "Forget cardio. Forget macros. Lift 3x for 8 weeks.") },
      { id: "two", duration: 2200, bg: dark, render: T({ kicker: "Step 2", lines: [{ text: "Hit" }, { text: "100g protein.", color: pink }], size: 150 }, "Don't track everything. Just protein. Daily.") },
      { id: "three", duration: 2200, bg: dark, render: T({ kicker: "Step 3", lines: [{ text: "Walk" }, { text: "8k.", color: pink }], size: 220 }, "Every day. Phone in pocket. Not the treadmill.") },
      { id: "wow", duration: 2000, bg: pink, render: T({ kicker: "That's it", lines: [{ text: "8 weeks." }, { text: "New body.", brush: true }], size: 160, kickerLight: true }) },
      cta("Want the plan"),
    ],
  },
  // 24 — What I eat in a day
  "24": {
    title: "WIEIAD",
    slides: [
      { id: "hook", duration: 2200, bg: pink, render: TT({ kicker: "WIEIAD", lines: [{ text: "What I" }, { text: "eat", brush: true }, { text: "in a day." }], size: 160 }) },
      { id: "am", duration: 1800, bg: dark, render: T({ kicker: "AM", lines: [{ text: "Eggs +" }, { text: "fruit.", color: pink }], size: 200 }, "3 eggs. Berries. Coffee. 35g protein.") },
      { id: "noon", duration: 1800, bg: dark, render: T({ kicker: "Noon", lines: [{ text: "Chicken" }, { text: "bowl.", color: pink }], size: 200 }, "Rice, greens, salsa, hot sauce. 50g protein.") },
      { id: "pre", duration: 1800, bg: dark, render: T({ kicker: "Pre-lift", lines: [{ text: "Banana +" }, { text: "shake.", color: pink }], size: 170 }, "Whey + dextrose. Quick fuel.") },
      { id: "pm", duration: 1800, bg: dark, render: T({ kicker: "PM", lines: [{ text: "Steak +" }, { text: "potatoes.", color: pink }], size: 150 }, "Big. Hot. No measuring. ~60g protein.") },
      { id: "n", duration: 1500, bg: pink, render: S({ kicker: "Total", value: "165g", sub: "Protein. Hit it daily.", kickerLight: true }) },
      cta(),
    ],
  },
  // 25 — Non-negotiables
  "25": {
    title: "Non-negotiables",
    slides: [
      { id: "hook", duration: 2200, bg: dark, render: TT({ kicker: "The 5", lines: [{ text: "My" }, { text: "non-", brush: true, color: pink }, { text: "negotiables." }], size: 160, kickerLight: false }) },
      { id: "n1", duration: 1600, bg: pink, render: S({ kicker: "01", value: "Lift 4x.", sub: "No matter what week it is.", kickerLight: true, brush: false }) },
      { id: "n2", duration: 1600, bg: dark, render: S({ kicker: "02", value: "Walk 8k.", sub: "Every. Single. Day.", kickerLight: false }) },
      { id: "n3", duration: 1600, bg: pink, render: S({ kicker: "03", value: "1g/lb.", sub: "Protein. No skipping.", kickerLight: true }) },
      { id: "n4", duration: 1600, bg: dark, render: S({ kicker: "04", value: "7 hrs.", sub: "Sleep, minimum.", kickerLight: false }) },
      { id: "n5", duration: 1600, bg: pink, render: S({ kicker: "05", value: "1 read.", sub: "30 min. Phone in another room.", kickerLight: true }) },
      cta(),
    ],
  },
  // 26 — Gym as therapy
  "26": {
    title: "Gym is therapy",
    slides: [
      { id: "hook", duration: 2400, bg: dark, full: true, render: P({ photo: "about", kb: "out", overlay: "b", kicker: "Real talk", lines: [{ text: "The gym" }, { text: "is", brush: true, color: pink }, { text: "therapy." }], size: 160 }) },
      { id: "why", duration: 2400, bg: dark, render: T({ kicker: "For me", lines: [{ text: "Heavy bar." }, { text: "Quiet head.", color: pink }], size: 140 }, "Nowhere else does my brain go this quiet.") },
      { id: "you", duration: 2400, bg: pink, render: T({ kicker: "For you", lines: [{ text: "It can be" }, { text: "the same.", brush: true }], size: 180, kickerLight: true }, "If you stop chasing aesthetics and start chasing strength.") },
      { id: "shift", duration: 2200, bg: dark, render: T({ kicker: "The shift", lines: [{ text: "Train for" }, { text: "peace.", color: pink }, { text: "Body follows." }], size: 130 }) },
      { id: "n", duration: 1800, bg: dark, render: S({ kicker: "Try it", value: "1 wk.", sub: "Lift 3 days. See the shift.", kickerLight: false }) },
      cta(),
    ],
  },
  // 27 — DM me 'PINK'
  "27": {
    title: "DM PINK",
    slides: [
      { id: "hook", duration: 2200, bg: pink, render: TT({ kicker: "Frictionless", lines: [{ text: "DM me" }, { text: "PINK.", brush: true }], size: 240 }) },
      { id: "what", duration: 2400, bg: dark, render: T({ kicker: "I'll send back", lines: [{ text: "The 7-day" }, { text: "plan.", color: pink }, { text: "Free." }], size: 160 }, "3-page PDF. Workouts. Macros. Walking targets.") },
      { id: "why", duration: 2200, bg: dark, render: T({ kicker: "Why", lines: [{ text: "Because" }, { text: "starting", brush: true, color: pink }, { text: "is the hard part." }], size: 130 }) },
      { id: "no", duration: 2200, bg: dark, render: L({ kicker: "What I won't do", items: ["Auto-DM you", "Spam you weekly", "Sell you a course"] }) },
      { id: "do", duration: 2000, bg: pink, render: S({ kicker: "What I will do", value: "Reply.", sub: "Personally. Same day. Promise.", kickerLight: true }) },
      cta("Hit my DMs"),
    ],
  },
  // 28 — Pull up before Q2
  "28": {
    title: "Pull up before Q2",
    slides: [
      { id: "hook", duration: 2200, bg: pink, render: TT({ kicker: "Last call", lines: [{ text: "Pull up" }, { text: "before", brush: true }, { text: "Q2." }], size: 180 }) },
      { id: "why1", duration: 2200, bg: dark, render: T({ kicker: "Why now", lines: [{ text: "Rates" }, { text: "rising.", color: pink }], size: 220 }, "Founders rate locks for everyone who starts before Q2.") },
      { id: "why2", duration: 2200, bg: dark, render: T({ kicker: "And", lines: [{ text: "Cohort" }, { text: "closes.", color: pink }], size: 220 }, "Next 90-day group doesn't open until April.") },
      { id: "what", duration: 2400, bg: dark, render: L({ kicker: "Lock in & get", items: ["90-day Slay program", "Founder rate forever", "Month 4 free on finish"] }) },
      { id: "n", duration: 1800, bg: pink, render: S({ kicker: "Open spots", value: "6", sub: "Out of 15. Move now.", kickerLight: true }) },
      cta("Sign me up"),
    ],
  },
};

// ---------------- Component ----------------

export default function PromoN() {
  const { n } = useParams();
  const reel = REELS[n];
  if (!reel) {
    return (
      <main className="font-body fixed inset-0 flex items-center justify-center bg-[#0A0A0A] text-white p-8">
        <div className="text-center" data-testid="promo-page" data-promo-active="missing">
          <p className="font-display font-black uppercase text-3xl tracking-tighter">Reel {n} not found.</p>
          <p className="text-white/60 mt-3">Try /promo/9 through /promo/28.</p>
        </div>
      </main>
    );
  }
  return <PromoStage slides={reel.slides} />;
}
